/**
 * usePeerLatency — round-trip time on the live WebRTC connections, polled from the browser.
 *
 * No extra traffic and no server involved: `RTCPeerConnection.getStats()` already measures the
 * path the video is taking, so turning the readout on costs a stats read rather than a ping.
 * That is what makes it safe to leave on over a wall of twenty players.
 *
 * Generic over WHERE the connections come from, because the two products differ: the cloud holds
 * one peer connection per node (its NodeConnectionManager), the local app holds exactly one to
 * its own SFU. Both hand this hook a list of `{ key, pc }` and read the answer back by key.
 *
 * RTT is a property of the CONNECTION, not of a camera — every camera streaming over one peer
 * connection shares one figure, and saying so is the point: it tells an operator the transport is
 * slow rather than blaming a camera.
 *
 * Disabled clears the map, so switching the overlay off can't leave a stale number on screen.
 */
import { useEffect, useState } from 'react';

export interface PeerLatency {
  /** Round-trip time in ms, from the succeeded ICE candidate pair. */
  rtt: number;
  /** Inbound video jitter in ms. */
  jitter: number;
  /** Cumulative inbound video packets lost. */
  packetsLost: number;
}

export interface LatencyPeer {
  key: string;
  pc: RTCPeerConnection | null | undefined;
}

const POLL_MS = 2000;

export function usePeerLatency(
  getPeers: () => LatencyPeer[],
  enabled: boolean,
): Map<string, PeerLatency> {
  const [latency, setLatency] = useState<Map<string, PeerLatency>>(new Map());

  useEffect(() => {
    if (!enabled) {
      // A badge that disappears is honest; a frozen figure is not.
      setLatency((prev) => (prev.size ? new Map() : prev));
      return;
    }
    let cancelled = false;

    const poll = async () => {
      const peers = getPeers();
      if (cancelled || peers.length === 0) return;

      const next = new Map<string, PeerLatency>();
      for (const { key, pc } of peers) {
        if (!pc) continue;
        try {
          const stats = await pc.getStats();
          stats.forEach((report: any) => {
            // The SELECTED candidate pair is the one carrying media; the others are rejected
            // alternatives whose RTT means nothing here.
            if (report.type === 'candidate-pair' && report.state === 'succeeded') {
              next.set(key, {
                rtt: Math.round((report.currentRoundTripTime || 0) * 1000),
                jitter: 0,
                packetsLost: 0,
              });
            }
            // Loss and jitter belong to the received stream, not to the path.
            if (report.type === 'inbound-rtp' && report.kind === 'video') {
              const existing = next.get(key);
              if (existing) {
                existing.packetsLost = report.packetsLost || 0;
                existing.jitter = Math.round((report.jitter || 0) * 1000);
              }
            }
          });
        } catch {
          /* the connection may be closing mid-poll — skip it this round */
        }
      }
      if (!cancelled) setLatency(next);
    };

    poll();
    const interval = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(interval); };
    // `getPeers` is a getter each caller keeps stable; depending on it would restart the poll
    // on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return latency;
}
